import { hasRole, type Role } from '@conversia/shared-types';
import { aParams, type EsquemaFiltros, type Filtros } from './url-filtros';
import { ESQUEMA_TRABAJOS } from './filtros-esquemas';
import type { TokenRango } from './rango-fechas';

/**
 * QUE CADA QUIEN VEA LO SUYO. PURO.
 *
 * Dos personas con el mismo rol de panel no miran lo mismo. El que está encima
 * de las garantías abre Trabajos todos los días y lo primero que hace es
 * filtrar; el resto de los administradores nunca filtran por ahí. El rol no
 * alcanza para distinguirlos, y por eso esta decisión vive en un archivo
 * propio y no repartida por las pantallas.
 *
 * ⚠️ **Hoy nadie llega marcado como encargado de garantías**: el panel todavía
 * no guarda ese dato de la persona (existe el rol `encargado_tecnicos` para los
 * números de aviso de WhatsApp, que es otra cosa). Cuando se guarde, este
 * archivo es el único lugar donde hay que tocarlo — el menú y la pantalla de
 * Desempeño ya preguntan acá.
 */

export interface QuienMira {
  /** Rol del panel. `undefined` mientras la sesión no resolvió. */
  rol: Role | undefined;
  /** Marcado como el que está encima de las garantías. */
  encargadoDeGarantias?: boolean;
}

export interface VistaInicial {
  ruta: string;
  filtros: Filtros;
}

/** El nombre del filtro de garantías en Trabajos, tal como viaja en la URL. */
export const FILTRO_GARANTIAS = 'garantia';

/**
 * Dónde arranca esta persona, y con qué filtros puestos. PURO.
 *
 * Lo que NO hace: mandar a alguien a una pantalla que le va a rebotar. Marcar
 * como encargado a un Lector no lo manda a Trabajos — ver la puerta cerrada es
 * peor que no ver la puerta.
 */
export function vistaInicial(quien: QuienMira): VistaInicial {
  const rol = quien.rol;
  if (rol === 'TECNICO') return { ruta: '/mis-trabajos', filtros: {} };

  if (quien.encargadoDeGarantias && rol && hasRole(rol, 'ADMIN')) {
    return { ruta: '/jobs', filtros: { [FILTRO_GARANTIAS]: true } };
  }

  return { ruta: '/', filtros: {} };
}

/**
 * El atajo «Garantías» del menú: Trabajos ya filtrado. `null` si Trabajos
 * todavía no acepta ese filtro. PURO.
 *
 * El `null` es el punto. El filtro de garantías lo agrega el trabajo de la
 * pantalla de Trabajos; mientras no exista, una entrada de menú que dice
 * «Garantías» y abre la lista completa MIENTE, y quien la toca se queda
 * creyendo que hay 400 garantías. Mejor no mostrar el atajo hasta que filtre
 * de verdad: el día que el filtro entre en el esquema, la entrada aparece sola.
 */
export function enlaceDeGarantias(esquema: EsquemaFiltros = ESQUEMA_TRABAJOS): string | null {
  if (!esquema[FILTRO_GARANTIAS]) return null;
  const qs = aParams({ [FILTRO_GARANTIAS]: true }, esquema).toString();
  return qs ? `/jobs?${qs}` : null;
}

/**
 * Con qué ventana de tiempo abre «Desempeño de técnicos» para esta persona.
 * PURO.
 *
 * El encargado de garantías la mira por semana porque está encima del tema y le
 * sirve ver el movimiento reciente. Para todos los demás abre por mes: con una
 * semana de instalaciones no se juzga a nadie.
 */
export function rangoInicialDeDesempeno(quien: QuienMira): TokenRango {
  return quien.encargadoDeGarantias ? 'semana' : 'mes';
}
